import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import Api from 'api';
import { JobAlertPreferences } from 'api/Api';
import TalentNavbar from 'components/TalentNavbar';
import TalentPageLayout from 'components/TalentPageLayout';
import TalentBackButton from 'components/TalentBackButton/TalentBackButton';
import { HYGIENIST_TYPES, UserAccountProps } from 'utils/types';
import styles from './JobAlertsPage.module.scss';

type Channel = 'inAppEnabled' | 'pushEnabled' | 'emailEnabled' | 'smsEnabled';
const CHANNELS: { key: Channel; label: string; description: string }[] = [
  { key: 'inAppEnabled', label: 'In-app', description: 'See alerts in your TempStars notification centre.' },
  { key: 'pushEnabled', label: 'Push', description: 'Get a notification on this device.' },
  { key: 'emailEnabled', label: 'Email', description: 'Get a message at your account email address.' },
  { key: 'smsEnabled', label: 'SMS', description: 'Get a text at your mobile number. Message rates may apply.' },
];
const RADII = [10, 25, 50, 75, 110];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const BASES = [
  { key: 'basisPermanent', value: 'permanent', label: 'Permanent' },
  { key: 'basisMaternity', value: 'maternity', label: 'Maternity Leave' },
  { key: 'basisContract', value: 'contract', label: 'Contract' },
] as const;

const analytics = (event: string, details: Record<string, unknown> = {}) => {
  Api.recordJobAlertInteraction(event, details).catch(error =>
    console.error('[JobAlerts] interaction could not be recorded', error));
};

export default function JobAlertsPage() {
  const history = useHistory();
  const [preferences, setPreferences] = useState<JobAlertPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [user, setUser] = useState<UserAccountProps | null>(null);

  useEffect(() => {
    let active = true;
    Api.getJobAlertPreferences()
      .then(result => { if (active) setPreferences(result); })
      .catch(() => { if (active) setError('Could not load your alert settings. Please try again.'); })
      .finally(() => { if (active) setLoading(false); });
    Api.getUser()
      .then(result => { if (active) setUser(result); })
      .catch(() => { if (active) setError('Could not load your account. Please try again.'); });
    analytics('Job Alerts Viewed');
    return () => { active = false; };
  }, []);

  const toggleChannel = async (channel: Channel) => {
    if (!preferences || saving) return;
    if (channel === 'smsEnabled' && !preferences.smsEnabled) {
      setConsentOpen(true);
      analytics('Job Alert SMS Consent Opened');
      return;
    }
    const selected = preferences[channel] ? 0 : 1;
    const next = { ...preferences, [channel]: selected };
    // Track the talent's tap even if the immediate final-channel save fails.
    analytics('Job Alert Channel Toggled', { channel, on: Boolean(selected) });
    if (!CHANNELS.some(({ key }) => next[key])) {
      next.enabled = 0;
      // The last channel must be saved immediately so leaving the page cannot
      // leave alerts active. Keep the server-confirmed state visible on failure.
      setSaving(true);
      setError('');
      try {
        setPreferences(await Api.updateJobAlertPreferences(next));
        setSaved(true);
      } catch (error: any) {
        setError(error?.message || 'Could not turn off Job Alerts. Please try again.');
      } finally {
        setSaving(false);
      }
      return;
    }
    setPreferences(next);
    setSaved(false);
  };

  const toggleMaster = () => {
    if (!preferences || saving) return;
    const enabled = preferences.enabled ? 0 : 1;
    setPreferences({ ...preferences, enabled });
    setSaved(false);
    setError('');
    analytics('Job Alerts Toggled', { on: Boolean(enabled) });
  };

  const selectRadius = (band: number) => {
    if (!preferences || saving) return;
    setPreferences({ ...preferences, radiusKm: band });
    setSaved(false);
    analytics('Job Alert Radius Set', { band });
  };

  const toggleDay = (index: number) => {
    if (!preferences || saving) return;
    const mask = preferences.daysOfWeek.split('');
    mask[index] = mask[index] === '1' ? '0' : '1';
    if (!mask.includes('1')) {
      setError('Choose at least one day of the week.');
      return;
    }
    const daysOfWeek = mask.join('');
    setPreferences({ ...preferences, daysOfWeek });
    setSaved(false);
    setError('');
    analytics('Job Alert Days Set', { mask: daysOfWeek });
  };

  const toggleBasis = (basis: typeof BASES[number]) => {
    if (!preferences || saving) return;
    const on = preferences[basis.key] ? 0 : 1;
    const next = { ...preferences, [basis.key]: on };
    if (!BASES.some(({ key }) => next[key])) {
      setError('Choose at least one position basis.');
      return;
    }
    setPreferences(next);
    setSaved(false);
    setError('');
    analytics('Job Alert Job Type Toggled', { basis: basis.value, on: Boolean(on) });
  };

  const save = async () => {
    if (!preferences) return;
    if (preferences.enabled && !CHANNELS.some(({ key }) => preferences[key])) {
      setError('Choose at least one way to receive Job Alerts.');
      return;
    }
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const updated = await Api.updateJobAlertPreferences({ ...preferences, smsConsent });
      setPreferences(updated);
      setSmsConsent(false);
      setSaved(true);
      analytics('Job Alert Preferences Saved', { enabled: Boolean(updated.enabled) });
    } catch (e: any) {
      setError(e?.message || 'Could not save your alert settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hygienist = user?.hygienist;
  const isHygOrAsst = hygienist?.type === HYGIENIST_TYPES.HYGIENIST
    || hygienist?.type === HYGIENIST_TYPES.ASSISTANT;

  return (
    <TalentPageLayout navbar={hygienist && <TalentNavbar
      firstName={hygienist.firstName || ''}
      lastName={hygienist.lastName || ''}
      photoUrl={hygienist.photoUrl || undefined}
      isHygOrAsst={isHygOrAsst}
      showAddShiftButton={false}
      showJobBoardButton={false}
      pageTitle="Job Alerts"
    />}>
      <div className={styles.page} data-testid="job-alerts-settings">
        <TalentBackButton onBack={() => history.goBack()} />
        <h1>Job Alerts</h1>
        <p>Choose how you hear about new Job Board positions that match your role and qualifications. Alerts are off until you turn them on.</p>
        {loading && <p role="status">Loading alert settings…</p>}
        {error && <p className={styles.error} role="alert">{error}</p>}
        {preferences && <>
          <label className={styles.row} htmlFor="job-alerts-enabled">
            <span><strong>Send me Job Alerts</strong><small>Independent from the job-seeking status on your profile.</small></span>
            <input id="job-alerts-enabled" data-testid="job-alerts-enabled" aria-label="Send me Job Alerts" type="checkbox" checked={Boolean(preferences.enabled)} disabled={saving} onChange={toggleMaster} />
          </label>
          <h2>Delivery channels</h2>
          <p>Choose at least one channel before turning alerts on.</p>
          {CHANNELS.map(({ key, label, description }) => <label className={styles.row} htmlFor={`job-alert-${key}`} key={key}>
            <span><strong>{label}</strong><small>{description}</small></span>
            <input id={`job-alert-${key}`} data-testid={`job-alert-${key}`} aria-label={label} type="checkbox" checked={Boolean(preferences[key])} disabled={saving} onChange={() => toggleChannel(key)} />
          </label>)}
          <h2>Match preferences</h2>
          <fieldset className={styles.options}>
            <legend>Distance from you</legend>
            {RADII.map(band => <label key={band}>
              <input type="radio" name="job-alert-radius" aria-label={`0–${band} km`} checked={preferences.radiusKm === band}
                onChange={() => selectRadius(band)} />0–{band} km
            </label>)}
          </fieldset>
          <fieldset className={styles.options}>
            <legend>Days of the week</legend>
            {DAYS.map((day, index) => <label key={day}>
              <input type="checkbox" aria-label={day} checked={preferences.daysOfWeek[index] === '1'}
                onChange={() => toggleDay(index)} />{day}
            </label>)}
          </fieldset>
          <fieldset className={styles.options}>
            <legend>Position basis</legend>
            {BASES.map(basis => <label key={basis.key}>
              <input type="checkbox" aria-label={basis.label} checked={Boolean(preferences[basis.key])}
                onChange={() => toggleBasis(basis)} />{basis.label}
            </label>)}
          </fieldset>
          <h2>More filters</h2>
          {[
            { label: 'Competitive Pay', filter: 'competitivePay' },
            { label: 'Has Benefits', filter: 'hasBenefits' },
          ].map(({ label, filter }) => <button
            key={filter} type="button" className={styles.comingSoon}
            onClick={() => analytics('Job Alert Coming Soon Tapped', { filter })}
          >{label}<span>(coming soon!)</span></button>)}
          <button type="button" className={styles.save} disabled={saving} onClick={save} data-testid="save-job-alerts">
            {saving ? 'Saving…' : 'Save Job Alerts'}
          </button>
          {saved && <p role="status">Your Job Alert settings are saved.</p>}
        </>}
      </div>
      {consentOpen && <div className={styles.backdrop} role="presentation">
        <section className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="sms-consent-title">
          <h2 id="sms-consent-title">Get Job Alerts by text?</h2>
          <p>By choosing Agree, you consent to receive automated SMS about matching Job Board positions from TempStars at your account mobile number. Consent is optional. Message and data rates may apply. Reply STOP to opt out or HELP for help.</p>
          <p>STOP pauses all TempStars service texts, including booking confirmations and Instabook, until you reply START. Push and email notifications can still arrive.</p>
          <div className={styles.dialogActions}>
            <button type="button" onClick={() => {
              setConsentOpen(false);
              analytics('Job Alert SMS Consent', { choice: 'declined' });
            }}>Cancel</button>
            <button type="button" data-testid="agree-sms-job-alerts" onClick={() => {
              setPreferences(current => current ? { ...current, smsEnabled: 1 } : current);
              setSmsConsent(true);
              setConsentOpen(false);
              setSaved(false);
              analytics('Job Alert SMS Consent', { choice: 'granted' });
              analytics('Job Alert Channel Toggled', { channel: 'smsEnabled', on: true });
            }}>Agree</button>
          </div>
        </section>
      </div>}
    </TalentPageLayout>
  );
}
